import { CommonModule } from '@angular/common'
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { MatMenuTrigger } from '@angular/material/menu'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CanvasService } from '../canvas.service'
import { CanvasArgs, CanvasCommand } from '../commands/canvas-command.model'
import { DeleteAllTracesCommand } from '../commands/delete-all-traces.command'
import { NewCanvasCommand } from '../commands/new-canvas.command'
import { DdcCanvasRowComponent } from '../ddc-canvas-row/ddc-canvas-row.component'
import { DdcCommandInvokerComponent } from '../ddc-command-invoker/ddc-command-invoker.component'
import { Canvas, CanvasTrace } from '../models/canvas.model'

@Component({
  selector: 'ddc-canvas-sidebar',
  templateUrl: './ddc-canvas-sidebar.component.html',
  styleUrls: ['./ddc-canvas-sidebar.component.scss'],
  imports: [
    CommonModule,
    AngularMaterialsModules,
    DdcCanvasRowComponent,
    DdcCommandInvokerComponent,
  ],
  standalone: true,
})
export class DdcCanvasSidebarComponent {
  @Input() canvases: Canvas[] = []
  @Input() selectedCanvasId: number | null = null
  @Input() dirtyCanvasIds: Set<number> = new Set()
  @Input() isLoading: boolean = false
  @Input() editingCanvasId: number | null = null
  @Input() traces: CanvasTrace[] = []
  @Input() selectedTraceId: number | null = null
  @Input() isLoadingTraces: boolean = false

  @Output() selectCanvas = new EventEmitter<Canvas>()
  @Output() createCanvas = new EventEmitter<void>()
  @Output() deleteCanvas = new EventEmitter<Canvas>()
  @Output() duplicateCanvas = new EventEmitter<Canvas>()
  @Output() renameCanvas = new EventEmitter<{ canvas: Canvas; title: string }>()
  @Output() refreshList = new EventEmitter<void>()
  @Output() selectTrace = new EventEmitter<CanvasTrace>()
  @Output() deleteTrace = new EventEmitter<CanvasTrace>()
  @Output() deleteAllTraces = new EventEmitter<void>()
  @Output() clearTrace = new EventEmitter<void>()

  @ViewChild(MatMenuTrigger) contextMenuTrigger: MatMenuTrigger
  @ViewChild('menuTriggerEl', { read: ElementRef }) menuTriggerEl: ElementRef<HTMLElement>

  contextMenuPosition = { x: 0, y: 0 }

  readonly newCanvasCommand: NewCanvasCommand
  readonly deleteAllTracesCommand: DeleteAllTracesCommand

  constructor(canvasService: CanvasService) {
    this.newCanvasCommand = new NewCanvasCommand(canvasService)
    this.deleteAllTracesCommand = new DeleteAllTracesCommand(canvasService)
  }

  argsForNewCanvas(): CanvasArgs {
    return {
      canvasId: this.selectedCanvasId,
      canvasTitle: '',
      hasUnsavedChanges: false,
      isSaving: false,
      action: () => this.createCanvas.emit(),
    }
  }

  argsForDeleteAllTraces(): CanvasArgs {
    return {
      canvasId: this.selectedCanvasId,
      canvasTitle: '',
      hasUnsavedChanges: false,
      isSaving: false,
      hasTraces: this.traces.length > 0,
      action: () => this.deleteAllTraces.emit(),
    }
  }

  onHeaderCommandExecuted(_command: CanvasCommand): void {
    // Client-side delegates handle the work; nothing to forward here.
  }

  onSelectCanvas(canvas: Canvas): void {
    this.selectCanvas.emit(canvas)
  }

  onCreateCanvas(): void {
    this.createCanvas.emit()
  }

  onDeleteCanvas(canvas: Canvas): void {
    this.deleteCanvas.emit(canvas)
  }

  onDuplicateCanvas(canvas: Canvas): void {
    this.duplicateCanvas.emit(canvas)
  }

  onCollapseAll(): void {
    // Placeholder for collapse all logic
  }

  onRefresh(): void {
    this.refreshList.emit()
  }

  onSelectTrace(trace: CanvasTrace): void {
    this.selectTrace.emit(trace)
  }

  onDeleteTrace(event: MouseEvent, trace: CanvasTrace): void {
    event.stopPropagation()
    this.deleteTrace.emit(trace)
  }

  onClearTrace(): void {
    this.clearTrace.emit()
  }

  onDeleteAllTraces(): void {
    this.deleteAllTraces.emit()
  }

  getTraceStatusIcon(status: string): string {
    switch (status) {
      case 'completed':
        return 'check_circle'
      case 'failed':
        return 'error'
      case 'running':
        return 'sync'
      default:
        return 'help'
    }
  }

  getTraceStatusClass(status: string): string {
    return `trace-status-${status}`
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault()

    const triggerEl = this.menuTriggerEl?.nativeElement
    if (triggerEl) {
      const parent = triggerEl.offsetParent as HTMLElement
      const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 }
      this.contextMenuPosition = {
        x: event.clientX - parentRect.left,
        y: event.clientY - parentRect.top,
      }
    } else {
      this.contextMenuPosition = { x: event.clientX, y: event.clientY }
    }

    this.contextMenuTrigger.openMenu()
  }
}
